import React, { useEffect, useState } from 'react';
import { Button, Nav } from 'react-bootstrap';
import { useTranslation } from "react-i18next";


import CentredSpinner from '../../Components/CentredSpinner';
import DataTable from '../../DataTable/datatable.component';
import DateColumn from '../../DataTable/datatable.datecolumn';
import SelectFilter from '../../DataTable/datatable.selectfilter';
import { DataTableFilter } from '../../DataTable/DataTableFilter';
import RecipeEditDialog from '../../Dialogs/Configuration/RecipeEditDialog';
import { Recipe } from '../../Types/Recipe';
import { useBusinessUnits } from '../../Utilities/useBusinessUnits';
import { useDateUtils } from '../../Utilities/useDateUtils';
import { useHttpUtils } from '../../Utilities/useHttpUtils';
import CommonConfigPage from './CommonConfigPage';

interface RecipeTableProps {
    showEditDialog: Function,
    includeDeleted: boolean,
    refresh: any,
    setFilters: any;
}

function RecipesTable({ showEditDialog, includeDeleted, refresh, setFilters }: RecipeTableProps) {
    const { fetchDataAuthenticated } = useHttpUtils();
    const [loaded, setLoaded] = useState(false);
    const [errored, setErrored] = useState(false);
    const [tableData, setTableData] = useState<Recipe[]>([]);

    useEffect(() => {
        const getRecipes = async function () {
            let url = `api/v1/recipe/?includeDeleted=${includeDeleted}`;

            await fetchDataAuthenticated(url)
                .then(response => {
                    if (response.ok) {
                        return response.json();
                    }
                    throw new Error('Something went wrong.');
                })
                .then(data => {
                    setTableData(data);
                    setErrored(false);
                    setLoaded(true);
                })
                .catch(error => {
                    setErrored(true);
                    setLoaded(false);
                });
        };

        getRecipes();
    }, [includeDeleted, refresh, fetchDataAuthenticated]);

    const tableColumns = [
        {
            Header: 'Name',
            accessor: 'name',
            filter: 'includesString',
            Cell: (props: any) => {
                return (<Nav><Nav.Link onClick={() => { showEditDialog(props.row.original); }}>{props.value}</Nav.Link></Nav>);
            }
        },
        {
            Header: 'Packout Grade',
            accessor: 'packoutGrade',
            filter: 'includesString',
        },
        {
            Header: 'Recipe Type',
            accessor: 'recipeTypeName',
            Filter: SelectFilter,
            filter: 'equals'
        },
        {
            Header: 'Equipment',
            accessor: 'equipmentName',
            Filter: SelectFilter,
            filter: 'equals'
        },
        {
            Header: 'Version',
            accessor: 'version',
            filter: 'includesString'
        },
        {
            Header: 'Prime?',
            accessor: (d: Recipe) => d.isPrime === true ? "Yes" : "No",
            id: 'isPrime',
            Filter: SelectFilter,
            options: ["Yes", "No"]
        },
        {
            Header: 'Grade Code',
            accessor: 'gradeCode',
            filter: 'includesString'
        },
        {
            Header: 'Default Material Code',
            accessor: 'defaultRecipeToPackageTypeMaterialCode',
            filter: 'includesString'
        },
        {
            Header: 'Auto Review',
            accessor: (d: Recipe) => d.allowAutoReview === true ? "Yes" : "No",
            id: 'allowAutoReview',
            Filter: SelectFilter,
            options: ["Yes", "No"]
        },
        {
            Header: 'Auto Grade',
            accessor: (d: Recipe) => d.allowAutoGrade === true ? "Yes" : "No",
            id: 'allowAutoGrade',
            Filter: SelectFilter,
            options: ["Yes", "No"]
        },
        {
            Header: 'Send Release Sample',
            accessor: (d: Recipe) => d.shouldSendReleaseSample === true ? "Yes" : "No",
            id: 'shouldSendReleaseSample',
            Filter: SelectFilter,
            options: ["Yes", "No"]
        },
        {
            Header: 'Last Change Account',
            accessor: 'lastChangeAccount',
            filter: 'includesString'
        },
        DateColumn({ header: 'Last Change', accessor: 'lastChangeUtc' }),
        {
            Header: 'Business Unit',
            accessor: 'businessUnitName',
            Filter: SelectFilter,
            filter: 'equals'
        }
    ];

    const getRowProps = (row: any) => {
        if (row.original.isDeleted)
            return { className: "deleted" };

        return {};
    };

    const initialState = {
        sortBy: [
            {
                id: 'name',
                desc: false
            }
        ]
    };

    return (loaded
        ? <DataTable tableColumns={tableColumns} tableData={tableData} initialState={initialState} getRowProps={getRowProps} setFilters={setFilters} autoResetPage={false} />
        : errored
            ? <p>There was a problem.</p>
            : <CentredSpinner />
    );
}

function RecipeConfiguration() {
    const { t } = useTranslation();
    const { fetchDataAuthenticated } = useHttpUtils();
    const { convertUTCToMedDateTimeSecs } = useDateUtils();
    const { businessUnits } = useBusinessUnits(false);
    const [filters, setFilters] = useState<DataTableFilter[]>([]);

    const [lastRecipeImportTime, setLastRecipeImportTime] = useState<string>("");

    let businessUnitFilter = filters.find((x: any) => x.id === "businessUnitName")?.value;

    useEffect(() => {
        const getLatestRecipeImportTime = async () => {
            await fetchDataAuthenticated("api/v1/recipe/recentRecipeImport")
                .then(async response => {
                    if (response.ok) {
                        if (response.status === 204) {
                            return Promise.resolve();
                        }
                        return response.json();
                    }
                    throw new Error(await response.json());
                })
                .then(data => {
                    if (data) {
                        setLastRecipeImportTime(convertUTCToMedDateTimeSecs(data) ?? "");
                    } else {
                        setLastRecipeImportTime(t("CommonResponses.NotFound"));
                    }
                })
                .catch(error => {
                    console.error(error.message);
                });
        };
        getLatestRecipeImportTime();

    }, [convertUTCToMedDateTimeSecs, fetchDataAuthenticated, t]);

    const getNewRecipe = () => {
        return { recipeId: null, name: "", businessUnitId: null, equipmentId: null, recipeTypeId: null, isDeleted: false };
    };

    const viewRecipeReport = () => {
        // This gets the business unit name from the filter

        if (businessUnitFilter) {

            let businessUnit = businessUnits.find(bu => bu.name == businessUnitFilter);

            if (businessUnit) {
                window.open(`${window.location.origin}/recipe/recipeReport/${businessUnit.code}`, '_blank', 'noreferrer');
            }
        }

        return;
    };

    const datasheetsButton = (
        <Button disabled={!businessUnitFilter} onClick={viewRecipeReport} className='ms-2'>Datasheets</Button>
    );

    return (
        <CommonConfigPage
            title="Recipes"
            Table={RecipesTable}
            Dialog={RecipeEditDialog}
            getNewConfigObject={getNewRecipe}
            pageMetaDataLabel={"Last updated:"}
            pageMetaData={lastRecipeImportTime}
            businessUnits={businessUnits}
            children={datasheetsButton}
            setFilters={setFilters}
        />
    );
}

export default RecipeConfiguration;